import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { useAppContextNonNull } from "@contexts/AppContext";
import ScreenOptionsEnum from "@models/ScreenOptionsEnum";
import EndpointsEnum from "@models/EndpointsEnum";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import IGetWarehousesDTO from "@models/DTO/IGetWarehousesDTO";
import IOnGetWarehousesDTO from "@models/DTO/IOnGetWarehousesDTO";
import { IFilterSection, IFilterSelectOption } from "@models/IFilterConfig";
import FeedbackService, { FEEDBACK_SOURCE_FILTER_BAR } from "@services/FeedbackService";
import { toQueryStringParts } from "@utils/apiQueryString";
import { useFilterPresets } from "@hooks/shared/useFilterPresets";
import { requestOnce, useBaseDto } from "@hooks/shared/useSocketLookups";
import {
    buildInventoryLogFilterSections,
    IInventoryLogFilterQuery,
    INVENTORY_LOG_DEFAULT_FILTERS,
    INVENTORY_LOG_SCOPE_PARAMS,
} from "@config/filters/inventoryLogFilters";
import FilterBar from "../../../shared/FilterBar/FilterBar";
import { useUrlFilters } from "../../../shared/FilterBar/useUrlFilters";

export const INVENTORY_LOG_FILTER_ACTIONS_ID = "inventory-log-filter-actions";

type Props = {
    catalogItemGuid: string;
    /** The order_guid / po_guid scope from an order or PO line link; kept in the URL when the bar rewrites it */
    scopeParams: { [param: string]: string };
    isSearching: boolean;
    /** Receives the query keyed by the `GET /v1/inventory/:catalogItemGuid/logs` params */
    searchFn: (query: IInventoryLogFilterQuery) => void;
};

/**
 * The supplier's warehouses as filter options, or null while they load. Without warehouses there is nothing to
 * load; a failed load leaves the warehouse filter out rather than blocking the page's first search.
 */
function useWarehouseOptions(): IFilterSelectOption[] | null {
    const { socket, supplierModel } = useAppContextNonNull();
    const baseDto = useBaseDto();
    const [options, setOptions] = useState<IFilterSelectOption[] | null>(supplierModel.has_warehouses ? null : []);

    useEffect(() => {
        if (!supplierModel.has_warehouses) {
            return;
        }

        let cancelled = false;
        // Vans hold stock too, so their log entries can be filtered by them.
        const payload: IGetWarehousesDTO = { ...baseDto(), includeVehicles: !!supplierModel.has_truck_inventory };

        requestOnce<IOnGetWarehousesDTO>(socket, EndpointsEnum.WarehouseGetWarehouses, EndpointsEnum.WarehouseOnGetWarehouses, payload)
            .then((data) => (data.warehouses || []).map((warehouse) => ({ label: warehouse.name, value: warehouse.guid })))
            .catch(() => [])
            .then((loaded) => {
                if (!cancelled) {
                    setOptions(loaded);
                }
            });

        return () => {
            cancelled = true;
        };
        // Once per page mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socket]);

    return options;
}

/**
 * Inventory log integration of the shared FilterBar (OR-1555). Screen key is ScreenOptionsEnum.INVENTORY_LOG
 * ("inventory_log"), which is also the presets JSON key. Mounts once the warehouse options have loaded, so a deep
 * link or preset with a warehouse sees the field.
 */
export const InventoryItemLogFilterBar = (props: Props) => {
    const { supplierModel } = useAppContextNonNull();
    const intl = useIntl();
    const warehouses = useWarehouseOptions();

    const formatMessage = useCallback(
        (id: string) => (intl.messages && intl.messages[id] !== undefined ? intl.formatMessage({ id }) : id),
        [intl]
    );

    const sections = useMemo(
        () => (warehouses ? buildInventoryLogFilterSections({ supplierModel, warehouses, formatMessage }) : null),
        [warehouses, supplierModel, formatMessage]
    );

    if (!sections) {
        return null;
    }

    return <InventoryItemLogFilterBarLoaded sections={sections} {...props} />;
};

/** The filter bar proper, mounted with its final sections. */
const InventoryItemLogFilterBarLoaded = (props: Props & { sections: IFilterSection[] }) => {
    const { sections, catalogItemGuid, scopeParams, searchFn } = props;
    const { userModel, supplierModel, vendorModel, uiContext } = useAppContextNonNull();
    const navigate = useNavigate();

    // Deep links (e.g. /inventory/items/:guid?date_from=2026-11-01&operation=1): read on mount and again on
    // Back / Forward. The order / PO scope params are not filters.
    const { initialFilters, historyKey, isFirstSearch } = useUrlFilters(sections, INVENTORY_LOG_SCOPE_PARAMS);

    const { presets, onPresetsChange } = useFilterPresets(ScreenOptionsEnum.INVENTORY_LOG);

    const handleSearch = useCallback(
        (query: { [apiKey: string]: unknown }) => {
            // Keep the URL in sync so a filtered log can be shared, keeping the scope from the line link.
            // The mount search replaces (no extra history entry); user searches push.
            const parts = [...toQueryStringParts(scopeParams), ...toQueryStringParts(query)];

            navigate(
                { pathname: `/inventory/items/${catalogItemGuid}`, search: parts.length ? `?${parts.join("&")}` : "" },
                { replace: isFirstSearch.current }
            );
            isFirstSearch.current = false;

            searchFn(query as IInventoryLogFilterQuery);
        },
        [navigate, searchFn, isFirstSearch, catalogItemGuid, scopeParams]
    );

    const handleFeedback = useCallback(
        async (message: string) => {
            try {
                await FeedbackService.sendFeedback(message, FEEDBACK_SOURCE_FILTER_BAR, { userModel, supplierModel, vendorModel });
                uiContext.notifyFn(null, { message: "feedback_sent", level: NotificationLevelEnum.success });
            } catch (error) {
                console.error("Failed to send FilterBar feedback", error);
                uiContext.notifyFn(null, { message: "feedback_failed", level: NotificationLevelEnum.error });
                throw error;
            }
        },
        [userModel, supplierModel, vendorModel, uiContext]
    );

    if (presets === null) {
        return null;
    }

    return (
        <FilterBar
            key={historyKey}
            sections={sections}
            presets={presets}
            onSearch={handleSearch}
            onPresetsChange={onPresetsChange}
            screenKey={ScreenOptionsEnum.INVENTORY_LOG}
            mode={"drawer"}
            defaultFilters={INVENTORY_LOG_DEFAULT_FILTERS}
            initialFilters={initialFilters}
            allLabel={"all_entries"}
            actionsContainerId={INVENTORY_LOG_FILTER_ACTIONS_ID}
            isSearching={props.isSearching}
            onSendFeedback={handleFeedback}
        />
    );
};

export default InventoryItemLogFilterBar;
